import { useCallback, useMemo } from 'react';
import { Product } from '~/lib/data-contract/salesforce';
import { useUtils } from '~/shared/hooks/useUtils';
import { useFrame } from '~/shared/utils';
import { useTranslation } from '~/shared/utils/translation';

type ProductPersonalizationProps = Partial<
    Pick<Product, 'c_personalization_metadata' | 'c_personalization_details' | 'c_personalization'>
> &
    Record<string, unknown>;

const useProductPersonalization = (props: ProductPersonalizationProps) => {
    const { data: frame } = useFrame();
    const { translate } = useTranslation();
    const { convertPriceToLocale } = useUtils();

    const { c_personalization_details, c_personalization_metadata } = props;

    const shouldAllowPersonalization =
        frame.productSettings?.shouldAllowPersonalization === undefined ||
        frame.productSettings?.shouldAllowPersonalization;

    const evaluatePrice = useCallback(
        (price: string | undefined) => (typeof price === 'string' ? parseFloat(price) : undefined),
        [],
    );

    const personalizationPrice = evaluatePrice(c_personalization_details?.price) ?? 0;
    // If base price is unavailable, default to final price.
    const basePersonalizationPrice =
        evaluatePrice(c_personalization_details?.basePrice) ?? personalizationPrice;

    const personalizationTitleWithPrice = translate('basket.personalization.title', {
        price: convertPriceToLocale(personalizationPrice),
    });

    const personalizationMetadata = useMemo(() => {
        try {
            if (c_personalization_metadata) {
                const parsedMetadata = JSON.parse(c_personalization_metadata);
                const isRecord = (value: unknown): value is Record<string, string> =>
                    typeof value === 'object' && !Array.isArray(value) && value !== null;

                if (isRecord(parsedMetadata) && Object.keys(parsedMetadata).length)
                    return parsedMetadata;
            }
        } catch (error) {
            console.error('Error: Personalization cannot be parsed.');
        }
    }, [c_personalization_metadata]);

    const personalizationMetadataEntries = useMemo(() => {
        if (!personalizationMetadata) return undefined;

        return Object.entries(personalizationMetadata).map(([key, value]) => ({
            key,
            value,
        }));
    }, [personalizationMetadata]);

    const addPersonalizationCostToPrice = useCallback(
        (price: number | string | undefined | null, shouldUseBaseCost?: boolean) => {
            let productPrice = 0;
            const evaluatedPersonalizationPrice = shouldUseBaseCost
                ? basePersonalizationPrice
                : personalizationPrice;

            if (typeof price === 'number') productPrice = price;

            if (typeof price === 'string') productPrice = parseFloat(price);

            return convertPriceToLocale(productPrice + evaluatedPersonalizationPrice);
        },
        [basePersonalizationPrice, convertPriceToLocale, personalizationPrice],
    );

    return {
        shouldAllowPersonalization,
        personalizationMetadata,
        personalizationMetadataEntries,
        personalizationPrice,
        personalizationTitleWithPrice,
        addPersonalizationCostToPrice,
    };
};

export default useProductPersonalization;
